import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { moneyMask, areaMask } from "@/shared/utils";
import {
  propertyBusinessType,
  propertyCategory,
  propertyCityState,
  propertyHeadline,
  propertyStatus,
  propertyTotalArea,
} from "@/shared/utils/property-display";
import { getCategoryLabel } from "../components/PropertyFormDualPane/schema";
import { LIST_PRICE_SUFFIX_KEY, resolveListPrice } from "@/shared/utils/format-property-price";
import type { PropertyResponseType } from "@services/housien/types";
import { Button, Text } from "@/shared/ui";
import { type ListingColumn } from "@/shared/ui/components/basic/listing-table";
import { StatusBadge } from "../components/StatusBadge";
import { BusinessTypeBadge } from "../components/BusinessTypeBadge";
import { getCategoryAccentColor } from "../components/category-accent";
import { PropertyThumbnail } from "../components/PropertyThumbnail";
import { PropertySpecs } from "../components/PropertySpecs";

/** Views a property as a plain record for dynamic field access. */
const asRecord = (p: PropertyResponseType) => p as unknown as Record<string, unknown>;

/** Right-aligned numeric cell: tabular figures so the digits line up down the column. */
const numericCell: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

/**
 * The identity cell: photo, title and the one line of context that decides
 * whether a row is worth opening.
 *
 * The status chip rides beside the title and only when the listing is *not*
 * active — an "Ativo" chip on every row of a list that is mostly active is a
 * column's worth of space spent saying nothing.
 *
 * @param p - The listing row.
 * @param t - Translator for labels and spec tooltips.
 * @returns The cell element.
 */
export function TitleCell({ p, t }: { p: PropertyResponseType; t: (key: string) => string }) {
  const loc = propertyCityState(asRecord(p));
  const category = propertyCategory(asRecord(p));
  const status = propertyStatus(asRecord(p));
  const city = [loc.city, loc.state].filter(Boolean).join(', ');

  return (
    // The compact density gives the row no vertical padding, so the row is
    // exactly as tall as this cell. The photo therefore has to bring its own
    // breathing room, or it sits flush against both row borders.
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
      minWidth: 0, padding: 'var(--space-2) 0',
    }}>
      <PropertyThumbnail property={p} width={96} height={72} category={category} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <Text
            component="span"
            style={{
              fontSize: 'var(--text-body-sm)',
              fontWeight: 'var(--font-semibold)' as unknown as number,
              lineHeight: 'var(--leading-snug)',
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {propertyHeadline(asRecord(p)) || t('list.noTitle')}
          </Text>
          {status !== 'active' && <StatusBadge status={status} compact />}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-meta)',
            lineHeight: 'var(--leading-snug)',
            color: 'var(--text-tertiary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Text component="span" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{city || '—'}</Text>
          <PropertySpecs property={p} t={t} />
        </div>
      </div>
    </div>
  );
}

/**
 * The price cell: the figure on top, the kind of deal underneath.
 *
 * Venda / Locação sits here as a legend rather than in a column of its own —
 * it is a property *of* the price, and reading it beside the number is how
 * "R$ 2.400/mês" stops being mistaken for a sale.
 *
 * @param p - The listing row.
 * @param t - Translator for the per-month suffix.
 * @returns The cell element.
 */
export function PriceCell({ p, t }: { p: PropertyResponseType; t: (key: string) => string }) {
  const price = resolveListPrice(asRecord(p));
  const amount = price?.amount;
  const suffixKey = price ? LIST_PRICE_SUFFIX_KEY[price.kind] : '';
  const suffix = suffixKey ? t(suffixKey) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: 0 }}>
      <Text
        component="span"
        style={{
          ...numericCell,
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--font-semibold)' as unknown as number,
          color: 'var(--text-primary)',
          letterSpacing: '-0.01em',
        }}
      >
        {amount ? moneyMask(amount.toFixed(2)).masked : '—'}
        {suffix && (
          <Text component="span" style={{ fontSize: 'var(--text-meta)', fontWeight: 500, color: 'var(--text-secondary)' }}>
            {suffix}
          </Text>
        )}
      </Text>
      <BusinessTypeBadge type={propertyBusinessType(asRecord(p))} variant="caption" />
    </div>
  );
}

/**
 * Builds the column definitions for the property table.
 *
 * @param t - Translator for list labels.
 * @param handleEditProperty - Opens the property editor for a row.
 * @returns Listing column definitions with nested/flat dual-read accessors.
 */
export function buildPropertyColumns(
  t: (key: string) => string,
  handleEditProperty: (property: PropertyResponseType) => void
): ListingColumn<PropertyResponseType>[] {
  return [
    {
      key: 'title',
      label: t('list.headers.property'),
      // Not server-sortable — the backend orders only by price. A `sortable`
      // header here would either silently sort one accumulated page (the
      // page-scoped bug) or need a server key that does not exist.
      render: (p) => <TitleCell p={p} t={t} />,
    },
    {
      key: 'category',
      label: t('list.headers.category'),
      width: '144px',
      // Not server-sortable (see the property column note above).
      render: (p) => {
        const category = propertyCategory(asRecord(p));
        return (
          <Text
            component="span"
            style={{
              fontWeight: 'var(--font-semibold)' as unknown as number,
              color: category ? getCategoryAccentColor(category) : undefined,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {getCategoryLabel(category ?? '', t) || '—'}
          </Text>
        );
      },
    },
    {
      key: 'total_area',
      label: t('list.headers.area'),
      width: '112px',
      align: 'right',
      // Not server-sortable (see the property column note above).
      render: (p) => {
        const area = propertyTotalArea(asRecord(p));
        // areaMask is a typing mask: it reads the digit stream and treats the
        // last two as decimals. String(92.8) is "92.8" -> 928 -> 9,28 m², a
        // tenfold understatement that only spared whole numbers. Feeding it
        // toFixed(2) is what the price column beside it already does.
        return (
          <Text component="span" style={numericCell}>
            {area ? `${areaMask(area.toFixed(2)).masked}` : '—'}
          </Text>
        );
      },
    },
    {
      key: 'price',
      label: t('list.headers.price'),
      width: '168px',
      align: 'right',
      // The one server-sortable column: maps to the backend `sale_price`
      // order key (see SERVER_SORT_BY_COLUMN in use-properties-data). The sort
      // is threaded into the request and resets to page 1, so it orders the
      // whole dataset server-side, never one accumulated page.
      sortable: true,
      render: (p) => <PriceCell p={p} t={t} />,
    },
    {
      key: 'actions',
      label: '',
      width: '48px',
      sortable: false,
      render: (p) => (
        <Button
          variant="none"
          disableSize
          onClick={(e) => { e.stopPropagation(); handleEditProperty(p); }}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          <DotsVerticalIcon width={16} height={16} />
        </Button>
      ),
    },
  ];
}
